import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/AdminQrPage.css';

function AdminQrPage({ mode = 'checkin' }) {
  const navigate = useNavigate();
  const isMonitor = mode === 'monitor';
  const actor = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(Number(localStorage.getItem('last_group_id') || 0));
  const [qrImage, setQrImage] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const selectedGroup = groups.find(group => Number(group.id) === Number(groupId));

  useEffect(() => {
    if (String(actor?.role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
    fetch(`${API_BASE}/api/groups`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('โหลดรายการก๊วนไม่สำเร็จ')))
      .then(data => {
        const list = Array.isArray(data) ? data : (data.groups || []);
        const fallbackGroup = isMonitor
          ? list[0]
          : list.find(group => String(group.Group_Status || '').toUpperCase() === 'OPEN');
        setGroups(list);
        setGroupId(current => list.some(group => Number(group.id) === Number(current)
          && (isMonitor || String(group.Group_Status || '').toUpperCase() === 'OPEN'))
          ? current
          : Number(fallbackGroup?.id || 0));
      })
      .catch(loadError => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [actor?.role, isMonitor, navigate]);

  const createQr = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError('');
    setQrImage('');
    try {
      let url;
      if (isMonitor) {
        url = `${window.location.origin}/monitor/${groupId}`;
      } else {
        const response = await fetch(`${API_BASE}/api/checkin/join/${groupId}/qr-code`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'สร้าง Check-in QR ไม่สำเร็จ');
        url = `${window.location.origin}/join/${groupId}?code=${encodeURIComponent(data.code)}`;
      }
      const image = await QRCode.toDataURL(url, {
        width: 520,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#102a43', light: '#ffffff' }
      });
      setTargetUrl(url);
      setQrImage(image);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, isMonitor]);

  useEffect(() => { createQr(); }, [createQr]);

  const chooseGroup = event => {
    const nextId = Number(event.target.value);
    setGroupId(nextId);
    if (nextId) localStorage.setItem('last_group_id', String(nextId));
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(targetUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <main className="admin-qr-page admin-shell-with-sidebar">
      <AdminSidebar active={isMonitor ? 'monitor-qr' : 'checkin-qr'} groupId={groupId} />
      <header>
        <div><small>ADMIN QR GENERATOR</small><h1>{isMonitor ? '📺 สร้าง QR Monitor' : '▦ สร้าง Check-in QR'}</h1></div>
        <button type="button" onClick={() => navigate('/dashboard')}>← Dashboard</button>
      </header>

      <section className="admin-qr-panel">
        <label>
          เลือกก๊วน
          <select value={groupId || ''} onChange={chooseGroup}>
            <option value="" disabled>เลือกก๊วน</option>
            {groups.map(group => (
              <option
                key={group.id}
                value={group.id}
                disabled={!isMonitor && String(group.Group_Status || '').toUpperCase() !== 'OPEN'}
              >
                {group.Group_Name} ({String(group.Group_Status || 'OPEN').toUpperCase()})
              </option>
            ))}
          </select>
        </label>

        <div className="admin-qr-card">
          {loading ? <p>กำลังสร้าง QR…</p> : error ? <p className="admin-qr-error">{error}</p> : qrImage ? <>
            <div className="admin-qr-heading"><b>{selectedGroup?.Group_Name || 'Group'}</b><span>{isMonitor ? 'สแกนเพื่อดูคิวและการแข่งขัน' : 'สแกนเพื่อ Check-in เข้าก๊วน'}</span></div>
            <img src={qrImage} alt={`${isMonitor ? 'Monitor' : 'Check-in'} QR ${selectedGroup?.Group_Name || ''}`} />
            <code>{targetUrl}</code>
            <div className="admin-qr-actions">
              <button type="button" onClick={copyLink}>{copied ? '✓ คัดลอกแล้ว' : '🔗 คัดลอกลิงก์'}</button>
              <a href={qrImage} download={`${isMonitor ? 'monitor' : 'checkin'}-${selectedGroup?.Group_Name || groupId}.png`}>⬇ ดาวน์โหลด QR</a>
              <a href={targetUrl} target="_blank" rel="noreferrer">↗ เปิดหน้าปลายทาง</a>
            </div>
          </> : <p>กรุณาเลือกก๊วน</p>}
        </div>
      </section>
    </main>
  );
}

export default AdminQrPage;
